import { Block } from "./types";

type CommandName =
    | "start_text"
    | "stop_text"
    | "start_entity"
    | "stop_entity"
    | "start_inline_style"
    | "stop_inline_style";

/**
 * A Command represents an operation at a specific character index.
 * Ranges are converted to start/stop command pairs.
 */
export class Command {
    constructor(
        public readonly name: CommandName,
        public readonly index: number,
        public readonly data: string = "",
    ) {}

    toString(): string {
        return `<Command ${this.name} ${this.index} ${this.data}>`;
    }

    /**
     * Create commands from a block's entity ranges.
     * Each range becomes a start_entity and stop_entity pair.
     */
    static fromEntityRanges(block: Block<string, string>): Command[] {
        const commands: Command[] = [];

        for (const range of block.entityRanges) {
            const data = String(range.key);
            const start = range.offset;
            const stop = start + range.length;
            commands.push(new Command("start_entity", start, data));
            commands.push(new Command("stop_entity", stop, data));
        }

        return commands;
    }

    /**
     * Create commands from a block's inline style ranges.
     * Each range becomes a start_inline_style and stop_inline_style pair.
     */
    static fromStyleRanges(block: Block<string, string>): Command[] {
        const commands: Command[] = [];

        for (const range of block.inlineStyleRanges) {
            const data = range.style;
            const start = range.offset;
            const stop = start + range.length;
            commands.push(new Command("start_inline_style", start, data));
            commands.push(new Command("stop_inline_style", stop, data));
        }

        return commands;
    }
}

/**
 * Build all commands for a block, sorted by index.
 */
function buildCommands(block: Block<string, string>): Command[] {
    const text = block.text;
    const styleCommands = Command.fromStyleRanges(block);
    const entityCommands = Command.fromEntityRanges(block);

    const commands = [
        new Command("start_text", 0),
        ...styleCommands,
        ...entityCommands,
    ];

    // Sort by index
    commands.sort((a, b) => a.index - b.index);

    // Add stop_text at the end
    commands.push(new Command("stop_text", text.length));

    return commands;
}

/**
 * Group commands by their index, paired with the text slice that follows.
 * Returns: Array of [textSlice, commands[]] tuples
 */
export function buildCommandGroups(
    block: Block<string, string>,
): [string, Command[]][] {
    const text = block.text;
    const commands = buildCommands(block);

    // Group by index
    const groups: Map<number, Command[]> = new Map();
    for (const cmd of commands) {
        const existing = groups.get(cmd.index);
        if (existing) {
            existing.push(cmd);
        } else {
            groups.set(cmd.index, [cmd]);
        }
    }

    // Convert to sorted array of [index, commands]
    const sortedGroups = Array.from(groups.entries()).sort(
        (a, b) => a[0] - b[0],
    );

    // Pair each group with the text slice that follows
    const result: [string, Command[]][] = [];
    for (let i = 0; i < sortedGroups.length; i++) {
        const [startIndex, cmds] = sortedGroups[i];
        const stopIndex =
            i < sortedGroups.length - 1 ? sortedGroups[i + 1][0] : text.length;
        const textSlice = text.slice(startIndex, stopIndex);
        result.push([textSlice, cmds]);
    }

    return result;
}
